import { platformBrowser } from '@angular/platform-browser';

import { environment } from './environments/environment';
import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { WidgetBootContext } from './app/core/widget/widget-config';
import {
    applyEmbeddedDocumentFlags,
    fetchTenantConfig,
    normalizeAuthCallbackLanding,
    parseBootContext,
} from './app/core/widget/widget-boot';

if (environment.production) {
    enableProdMode();
}

/**
 * One-time wipe of pre-tenant-prefix auth storage. Keys without the
 * t:<tenantId>: prefix predate the multi-tenant isolation fix and may belong
 * to any tenant. Also removes vestigial Auth0 SPA SDK keys — Auth0 is retired;
 * ol-auth is the provider now.
 *
 * Other tenants' t:<otherId>:* keys are intentionally left alone — wiping them
 * would cause two browser tabs on different tenants to fight on each load.
 */
function wipeLegacyAuthStorage(): void {
    try {
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (k.startsWith('oidc.')) toRemove.push(k);
            if (k.startsWith('@@auth0spajs@@')) toRemove.push(k);
        }
        toRemove.forEach((k) => localStorage.removeItem(k));
        if (toRemove.length > 0) {
            console.log(`[web-customer-app] Cleared ${toRemove.length} legacy auth-storage key(s) (one-time migration)`);
        }
    } catch {
        // Embedded widgets do not rely on third-party local storage.
    }
}

function show404(slug: string | null): void {
    const container = document.createElement('div');
    container.setAttribute('data-testid', 'unknown-tenant');
    container.style.cssText = 'max-width:500px;margin:100px auto;border:1px solid #e53935;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,0.1)';
    const header = document.createElement('div');
    header.style.cssText = 'background:#e53935;color:white;padding:12px 16px;font-size:1.25rem;font-weight:bold';
    header.textContent = 'Tenant not found';
    const body = document.createElement('div');
    body.style.cssText = 'text-align:center;padding:32px 16px;color:#333';
    body.textContent = slug
        ? `No booker is configured for "${slug}".`
        : 'No tenant slug in the URL.';
    container.appendChild(header);
    container.appendChild(body);
    document.body.appendChild(container);
}

function showError(err: unknown): void {
    const container = document.createElement('div');
    container.style.cssText = 'max-width:500px;margin:100px auto;border:1px solid #e53935;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,0.1)';
    const header = document.createElement('div');
    header.style.cssText = 'background:#e53935;color:white;padding:12px 16px;font-size:1.25rem;font-weight:bold';
    header.textContent = 'Something went wrong';
    const body = document.createElement('div');
    body.style.cssText = 'text-align:center;padding:32px 16px;color:#333';
    body.textContent = 'Failed to load application. Please try again later.';
    container.appendChild(header);
    container.appendChild(body);
    document.body.appendChild(container);
    console.error('Application bootstrap failed:', err);
}

/**
 * Walk from a wheel event's target up the ancestor chain looking for an
 * element that can still scroll in the wheel's direction (auto/scroll overflow
 * AND not at the boundary). Returns the first such element, or null if none.
 * Used by the iframe-mode wheel forwarder to skip events that an internal
 * scrollable element (open dropdown list, etc.) will handle natively — forwarding
 * those would double-scroll: inner element + parent page.
 */
function findScrollableAncestor(start: Element | null, deltaY: number, deltaX: number): Element | null {
    let el: Element | null = start;
    while (el && el !== document.documentElement) {
        const cs = window.getComputedStyle(el);
        const yScrollable = cs.overflowY === 'auto' || cs.overflowY === 'scroll';
        const xScrollable = cs.overflowX === 'auto' || cs.overflowX === 'scroll';
        if (deltaY !== 0 && yScrollable) {
            if (deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight) return el;
            if (deltaY < 0 && el.scrollTop > 0) return el;
        }
        if (deltaX !== 0 && xScrollable) {
            if (deltaX > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth) return el;
            if (deltaX < 0 && el.scrollLeft > 0) return el;
        }
        el = el.parentElement;
    }
    return null;
}

/**
 * Forward wheel deltas to the operator's page via postMessage.
 *
 * Chrome and Firefox do not propagate wheel events from a cross-origin iframe
 * to the parent, even when the iframe has nothing to scroll. iframe-resizer
 * sizes the booker iframe to content height, so the iframe document itself
 * has no overflow — but wheel events over the iframe area are silently
 * consumed by the browser. Forward the deltas so the parent page can
 * window.scrollBy. The parent listens for openlimo:scroll messages.
 *
 * Skipped when not framed, and when an ancestor of the wheel target can still
 * scroll in the wheel direction (an internal scroll region such as an open
 * dropdown list).
 *
 * Falls back to '*' as targetOrigin when ctx.parentOrigin is null (operator
 * page with a strict Referrer-Policy). The wheel deltas are non-sensitive and
 * the parent validates the sender's origin on receive.
 *
 * Wheel events fire at native scroll rate (~30-60/sec). Accumulate deltas and
 * post once per animation frame instead of per event — the parent applies a
 * single window.scrollBy of the accumulated delta per frame, which produces
 * identical scroll distance with one postMessage per frame instead of many.
 */
function installIframeWheelForwarder(ctx: WidgetBootContext): void {
    if (!ctx.isEmbedded) return;
    const parentOrigin = ctx.parentOrigin ?? '*';
    let accumulatedX = 0;
    let accumulatedY = 0;
    let framePending = false;
    window.addEventListener('wheel', (e: WheelEvent) => {
        const target = e.target instanceof Element ? e.target : null;
        if (findScrollableAncestor(target, e.deltaY, e.deltaX)) return;
        accumulatedX += e.deltaX;
        accumulatedY += e.deltaY;
        if (framePending) return;
        framePending = true;
        requestAnimationFrame(() => {
            window.parent.postMessage(
                { type: 'openlimo:scroll', deltaX: accumulatedX, deltaY: accumulatedY },
                parentOrigin,
            );
            accumulatedX = 0;
            accumulatedY = 0;
            framePending = false;
        });
    }, { passive: true });
}

async function bootstrap(): Promise<void> {
    const ctx = parseBootContext();
    window.__OPENLIMO_BOOT_CONTEXT__ = ctx;

    applyEmbeddedDocumentFlags(ctx);
    normalizeAuthCallbackLanding(ctx);
    wipeLegacyAuthStorage();
    installIframeWheelForwarder(ctx);

    // Standalone non-/embed/ URL (e.g. my-test.open.limo/customer). Boot with
    // no tenantConfig; environment.defaultTenant kicks in via existing fallbacks.
    if (!ctx.envPath) {
        await bootstrapApp();
        return;
    }

    // /embed/<env>/(index.html)? landing — no slug in the URL. Reached after a
    // /logout round-trip. AuthorizationService.logout() stashes the active slug
    // to sessionStorage('booker_post_logout_slug') before calling
    // signoutRedirect; recover it here and reload into the slug-scoped URL so
    // the SPA boots fresh with the right tenant.
    if (!ctx.slug) {
        let postLogoutSlug: string | null = null;
        try { postLogoutSlug = sessionStorage.getItem('booker_post_logout_slug'); } catch { /* unavailable */ }
        if (postLogoutSlug && ctx.envPath) {
            try { sessionStorage.removeItem('booker_post_logout_slug'); } catch { /* unavailable */ }
            window.location.replace(ctx.envPath + postLogoutSlug + '/');
            return;
        }
        show404(null);
        return;
    }

    const tenantConfig = await fetchTenantConfig(ctx.slug, ctx.envPath);
    if (!tenantConfig) {
        show404(ctx.slug);
        return;
    }
    window.tenantConfig = tenantConfig;

    await bootstrapApp();
}

async function bootstrapApp(): Promise<void> {
    const { AppModule } = await import('./app/app.module');
    await platformBrowser()
        .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] });
}

bootstrap().catch(showError);
